import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, Share2, Zap } from 'lucide-react';
import { useQuery } from 'react-query';
import { supabase } from '../../lib/supabaseClient';
import { Analysis } from '../../types/analysis';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export const SharedAnalysis: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  
  const { data: analysis, isLoading, error } = useQuery<Analysis>(
    ['shared-analysis', shareId],
    async () => {
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .eq('share_id', shareId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Analysis not found');
      
      return data;
    },
    {
      enabled: !!shareId,
      staleTime: Infinity,
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-3xl">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-3xl text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Analysis Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            The analysis you're looking for might have been removed or is no longer available.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <Search className="w-5 h-5 mr-2" />
              Try Your Own Analysis
            </Link>
            <Link
              to="/markets"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Explore Markets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-3xl">
        {/* Top CTA */}
        <div className="text-center mb-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors group"
          >
            <Zap className="w-6 h-6 mr-2 group-hover:animate-pulse" />
            <span className="text-lg">Try Free Analysis</span>
          </Link>
        </div>

        {/* Analysis Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 transition-colors">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Shared Analysis
            </h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Query: {analysis.query}
            </p>
          </div>

          <div className="text-gray-700 dark:text-gray-200 break-words whitespace-pre-wrap text-sm leading-relaxed 
            [&_img]:inline [&_img]:h-4 [&_img]:w-4 [&_img]:align-middle [&_img]:mx-0.5
            [&_emoji]:inline [&_emoji]:align-middle [&_emoji]:leading-none [&_emoji]:text-base">
            {analysis.result}
          </div>

          {/* Bottom CTA Section */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Want to try it yourself?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto">
              Get instant football match analysis powered by AI
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors group"
              >
                <Zap className="w-6 h-6 mr-2 group-hover:animate-pulse" />
                <span className="text-lg">Try Free Analysis</span>
              </Link>
              <Link
                to="/markets"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Share2 className="w-6 h-6 mr-2" />
                <span className="text-lg">Explore Markets</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};